import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useFocusTrap } from '@/hooks/useFocusTrap';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  isModal?: boolean;
}

export function BottomSheet({ isOpen, onClose, children, className = '', style = {}, isModal = true }: BottomSheetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sheetHeight, setSheetHeight] = useState<number | 'auto'>('auto');
  
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      setSheetHeight('auto');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  useFocusTrap(containerRef as React.RefObject<HTMLElement | null>, isOpen);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    e.stopPropagation();
    
    const startY = e.clientY;
    const startHeight = containerRef.current.getBoundingClientRect().height;
    
    document.body.style.userSelect = 'none';
    
    const onPointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      const deltaY = startY - moveEvent.clientY;
      const newHeight = startHeight + deltaY;
      const maxHeight = window.innerHeight * 0.95;
      setSheetHeight(Math.max(0, Math.min(maxHeight, newHeight)));
    };
    
    const onPointerUp = (upEvent: PointerEvent) => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      
      const deltaY = startY - upEvent.clientY;
      const finalHeight = startHeight + deltaY;
      
      if (finalHeight < 150 || Math.abs(deltaY) < 5) {
        onClose();
      }
    };
    
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[99998] bg-slate-900/50 backdrop-blur-sm sm:hidden"
          />
          <motion.div
            ref={containerRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: "spring", damping: 26, stiffness: 300, mass: 0.8 }}
            tabIndex={-1}
            style={{ 
              ...(sheetHeight !== 'auto' ? { height: `${sheetHeight}px` } : {}),
              ...style 
            }}
            className={`fixed sm:hidden bottom-0 left-0 right-0 z-[99999] w-full bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 rounded-t-2xl flex flex-col max-h-[95vh] pointer-events-auto shadow-[0_-10px_40px_rgba(0,0,0,0.1)] ${className}`}
          >
            {/* Handle */}
            <div onPointerDown={handlePointerDown} className="flex justify-center p-3 shrink-0 cursor-grab active:cursor-grabbing touch-none select-none">
              <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full pointer-events-none" />
            </div>
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
